import { Injectable, ComponentRef } from '@angular/core';

import { ModalComponent, ModalService, ModalType } from '@ipreo/ngx-sprinkles';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ExportModalComponent } from '../components/export-modal/export-modal.component';
import { ExportFacade } from '../facades/export.facade';
import { ExportModalOpened } from '../state/allotments-export/actions';
import {
  PendoBidCompExport,
  PendoDealMonitorExport,
  PendoDealShowExport,
  PendoTrackingActionTypes
} from '../state/pendo/actions';
import {
  CancelAction,
  ExportAction,
  ExportModalAction,
  ExportModalOption,
  ExportServiceOption
} from '../types/export-modal.types';
import { generateExcelWorkBook } from '../utils/export/excel/workbook';
import { ExcelWorkSheet } from '../utils/export/excel/worksheet';

@Injectable()
export class ExportModalService {
  private modalRef: ComponentRef<ModalComponent>;

  constructor(
    protected modalService: ModalService,
    private exportFacade: ExportFacade,
    private store: Store
  ) {}

  public handleExport(
    pendoAction:
      | PendoTrackingActionTypes.DealMonitorExport
      | PendoTrackingActionTypes.DealShowExport
      | PendoTrackingActionTypes.BidCompExport,
    dealId?: string
  ) {
    this.exportFacade.exportOptions$
      .pipe(
        take(1),
        switchMap((options: ExportServiceOption[]) =>
          this.openExportModal(options)
        ),
        filter(
          (exportAction) => exportAction.action !== ExportModalAction.cancel
        ),
        map((e: ExportAction) =>
          e.options.filter((option) => option.isChecked)
        ),
        switchMap((selectedOptions: ExportModalOption[]) => {
          return combineLatest(
            this.exportFacade.exportWorksheets(selectedOptions),
            this.exportFacade.getFilename()
          );
        })
      )
      .subscribe(([worksheets, filename]: [ExcelWorkSheet[], string]) => {
        void generateExcelWorkBook(filename, worksheets);
        let action;
        switch (pendoAction) {
          case PendoTrackingActionTypes.DealMonitorExport:
            action = new PendoDealMonitorExport();
            break;

          case PendoTrackingActionTypes.DealShowExport:
            action = new PendoDealShowExport(dealId);
            break;

          case PendoTrackingActionTypes.BidCompExport:
            action = new PendoBidCompExport();
            break;

          default:
            action = undefined;
            break;
        }
        if (action) {
          this.store.dispatch(action);
        }
      });
  }

  private openExportModal(
    options: ExportServiceOption[]
  ): Observable<ExportAction | CancelAction> {
    this.store.dispatch(new ExportModalOpened());

    this.modalRef = this.modalService.create({
      showOverlay: true,
      closeOnOverlayClick: true,
      title: 'Export Options',
      context: { options: this.mapToExportModalOptions(options) },
      type: ModalType.View,
      view: ExportModalComponent,
      width: 300
    });

    return this.modalRef.instance.response;
  }

  public closeModal(): void {
    if (this.modalRef) {
      this.modalRef.destroy();
      this.modalRef = undefined;
    }
  }

  public mapToExportModalOptions(
    options: ExportServiceOption[]
  ): ExportModalOption[] {
    return options.map((option) => ({
      ...option,
      isChecked: option.isCheckedByDefault,
      configOptions: option.configOptions.map((configOption) => ({
        ...configOption,
        isChecked: option.isCheckedByDefault && configOption.isCheckedByDefault
      }))
    }));
  }
}
